import { Partner } from './partner';
import { catchError, map } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { Injectable } from '@angular/core';
import { ApiService } from './api';
import { RoleType } from '../admin/role-type.enum';
import { FullstoryService } from './fullstory.service';
import { jwtDecode } from 'jwt-decode';
import { Store } from '@ngrx/store';
import { resetUser } from '@hub-store/user/user.actions';
import { AuthService } from '../auth/auth.service';

export interface User {
  id?: number;
  avatar_large_url?: string;
  avatar_thumb_url?: string;
  role: RoleType;
  scope: string;
  permissions?: Array<string>;
  is_superuser?: boolean;
  accepted_terms: boolean;
  name?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  isCommunityParticipant?: boolean;
  partner_code?: string;
  org_access?: string[];
  homeAccountId?: string;
}

@Injectable({ providedIn: 'root' })
export class UserService {
  path: string = '/api';
  count = 0;
  constructor(
    private apiService: ApiService,
    private authService: AuthService,
    private fullstoryService: FullstoryService,
    private store: Store,
  ) { }

  private getClaimValue(claims: any, keys: string[]): string {
    for (const key of keys) {
      const value = claims?.[key];
      if (typeof value === 'string' && value.trim().length > 0) {
        return value.trim();
      }
    }
    return '';
  }

  private getNamesFromClaims(claims: any): { first_name: string; last_name: string } {
    const parsedName = (claims?.name || '').trim().split(/\s+/, 2);
    const parsedFirstName = parsedName[0] || '';
    const parsedLastName = parsedName[1] || '';

    return {
      first_name: this.getClaimValue(claims, ['first_name']) || parsedFirstName,
      last_name: this.getClaimValue(claims, ['last_name']) || parsedLastName,
    };
  }

  private setUser(): Observable<User | null> {
    // No catchError by design: acquireApiTokenOrReauth handles every auth-required
    // failure (redirect + sentinel). A null user here means "authenticated, no
    // recognized role" — the guards' only access-denied trigger.
    return this.authService.acquireApiTokenOrReauth().pipe(
      map(result => {
        const decoded: any = jwtDecode(result.accessToken);
        const user = this.mapUmsUserToInternalUserData(result.account, decoded.roles || []);
        if (user) {
          localStorage.setItem('currentUser', JSON.stringify(user));
        }
        return user;
      })
    );
  }

  public getUser(cache: boolean = true): Observable<User | null> {
    if (!cache) {
      return this.setUser();
    }

    const storedUser = localStorage.getItem('currentUser');
    if (!storedUser) {
      return this.setUser();
    }

    try {
      const user = JSON.parse(storedUser);
      // Only trust the cache if it belongs to the active MSAL account; otherwise
      // re-derive so a stale or foreign cached user can't drive guard decisions.
      return user && this.isCacheForActiveAccount(user) ? of(user) : this.setUser();
    } catch {
      return this.setUser();
    }
  }

  private isCacheForActiveAccount(user: User): boolean {
    const account = this.authService.getActiveOrFirstAccount();
    return !!account && user.homeAccountId === account.homeAccountId;
  }

  public clearCachedUser(): void {
    localStorage.removeItem('currentUser');
    this.store.dispatch(resetUser());
  }

  public getTutorialCompletedFlag(): Observable<boolean> {
    return this.apiService.get<{ data: { welcome_tutorial_completed: boolean } }>(`${this.path}/v2/users/tutorialCompletedFlag`).pipe(map(resp => resp.data.welcome_tutorial_completed));
  }

  public updateTutorialCompletedFlag(): Observable<boolean> {
    return this.apiService.put<{ data: { welcome_tutorial_completed: boolean } }>(`${this.path}/v2/users/tutorialCompletedFlag`, true).pipe(map(resp => resp.data.welcome_tutorial_completed));
  }

  public getUsers(): Observable<User[]> {
    return this.apiService.get<User[]>(`${this.path}/users`);
  }

  public recordSignIn(): Observable<any> {
    return this.apiService.post(`${this.path}/v2/users/sign_in`, {}).pipe(
      catchError((err) => {
        console.warn('[UserService] recordSignIn failed', err?.status);
        return of(null);
      })
    );
  }

  public getUsersByOrgCode(orgCode: string): Observable<User[]> {
    return this.apiService.get(`${this.path}/v2/users?org_code=${orgCode}`).pipe(map((resp: any) => resp.data));
  }

  public getUsersByOrgAccess(orgName: string): Observable<User[]> {
    return this.apiService.get(`${this.path}/v2/users?org_access=${orgName}`).pipe(map((resp: any) => resp.data));
  }

  public getUserById(id: number) {
    return this.apiService.get(`${this.path}/users/${id}/profile`);
  }

  public getCurrentUserProfile() {
    return this.apiService.get(`${this.path}/v2/users/profile`);
  }

  public deleteUser(id: number, partnerId: any) {
    return this.apiService.delete(`${this.path}/airlines/${partnerId}/users/${id}`);
  }

  public createUser(data: FormData): Observable<User> {
    return this.apiService.postForm<User>(`${this.path}/users`, data);
  }

  public updateUserById(id: number, args: any): Observable<User> {
    return this.apiService.put<User>(`${this.path}/users/${id}/profile`, { data: args });
  }

  public updateProfile(profileData: FormData) {
    return this.apiService
      .putForm<User>(`${this.path}/v2/users/profile`, profileData);
  }

  public updateUser(): Observable<User | null> {
    return this.setUser();
  }

  public getAirlineUsers(airlineId: string): Observable<User[]> {
    return this.apiService.get<User[]>(`${this.path}/airlines/${airlineId}/users`);
  }

  public getAdminUsers(): Observable<User[]> {
    return this.apiService.get<User[]>(`${this.path}/admins`).pipe(catchError(() => of([])));
  }

  public getInternalContentUsers(): Observable<User[]> {
    return this.apiService.get<any[]>(`${this.path}/v2/internal_content_users`).pipe(
      map(users =>
        users.map(user => ({
          ...user,
          name: user.full_name,
          role: RoleType.ADMIN,
          partner_code: 'ATPCO'
        }))
      ),
      catchError(() => of([]))
    );
  }

  getPermissionDisplayChangePartner(user: User): boolean {
    return user.role === RoleType.ADMIN || (user.org_access?.length ?? 0) > 1;
  }

  getPermissionDisplayFooterUasLinks(user: User): boolean {
    return user.scope === 'airline';
  }

  getPermissionIsAirline(user: User): boolean {
    return user.scope === 'airline';
  }

  getPermissionDisplayGalleryLinks(user: User): boolean {
    return user.scope === 'airline' && user.role !== RoleType.OBSERVER;
  }

  getPermissionDisplayBundlesLinks(user: User): boolean {
    return user.scope === 'airline' && user.role !== RoleType.OBSERVER;
  }

  // FE role × subscription-tier calculation, mirroring the BE projection in
  // api/lib/hub_cms_api/requests/users/show.rb. Computed per selected partner.
  private readonly TIERS_ANY = ['Seat only', 'Starter', 'Pro', 'Expert', 'Enterprise', 'Trial'];
  private readonly TIERS_UTA = ['Pro', 'Expert', 'Enterprise', 'Trial'];

  private hasRecognizedTier(partner?: Partner): boolean {
    return !!partner?.subscription_type && this.TIERS_ANY.includes(partner.subscription_type);
  }

  private hasUtaTier(partner?: Partner): boolean {
    return !!partner?.subscription_type && this.TIERS_UTA.includes(partner.subscription_type);
  }

  private hasManageTargeting(user: User): boolean {
    return [
      RoleType.ADMIN,
      RoleType.COMMUNITY_PARTICIPANT,
      RoleType.COLLABORATOR,
      RoleType.SUPERUSER,
    ].includes(user.role);
  }

  getPermissionDisplayListsLinks(user: User): boolean {
    return user.scope === 'airline' && this.hasManageTargeting(user);
  }

  getPermissionDisplaySettingsLinks(user: User): boolean {
    return (
      user.scope === 'airline' &&
      [RoleType.ADMIN, RoleType.SUPERUSER, RoleType.COMMUNITY_PARTICIPANT].includes(user.role)
    );
  }

  getPermissionDisplaySeatsLinks(user: User): boolean {
    return user.scope === 'airline' && user.role !== RoleType.OBSERVER;
  }

  getPermissionUaTargeting(user: User): boolean {
    return this.hasManageTargeting(user);
  }

  getPermissionUaPublishing(user: User): boolean {
    return user.scope === 'airline' && (user.role === RoleType.ADMIN || user.role === RoleType.SUPERUSER);
  }

  getPermissionShowDisable(user: User): boolean {
    return [RoleType.ADMIN, RoleType.SUPERUSER].includes(user.role);
  }

  getPermissionShowAirlineChannels(user: User, partner?: Partner): boolean {
    if (user.scope !== 'airline') return false;
    if (user.role === RoleType.ADMIN || user.role === RoleType.COMMUNITY_PARTICIPANT) return true;
    // Remove tier checking due to inconsistent backend handling
    if (user.role === RoleType.SUPERUSER) return true;
    return false;
  }

  getPermissionShowAirlineUpas(user: User, partner?: Partner): boolean {
    if (user.role === RoleType.ADMIN || user.role === RoleType.COMMUNITY_PARTICIPANT) return true;
    // Remove tier checking due to inconsistent backend handling
    if (user.role === RoleType.COLLABORATOR || user.role === RoleType.SUPERUSER) {
      return true;
    }
    return false;
  }

  getPermissionShowAirlineUtas(user: User, partner?: Partner): boolean {
    if (user.role === RoleType.ADMIN || user.role === RoleType.COMMUNITY_PARTICIPANT) return true;
    // Remove tier checking due to inconsistent backend handling
    if (user.role === RoleType.COLLABORATOR || user.role === RoleType.SUPERUSER) {
      return true;
    }
    return false;
  }

  getPermissionCommunityParticipant(user: User): boolean {
    return user.role === RoleType.COMMUNITY_PARTICIPANT;
  }

  getPermissionShowUtasV1(user: User, airline: Partner) {
    return (
      user.scope === 'airline' &&
      user.role === RoleType.ADMIN &&
      airline.uta_source === 'hub'
    );
  }

  setFullstoryUser(user: User) {
    let org = 'no org found';
    if (user.role === RoleType.ADMIN) {
      org = 'ATPCO';
    } else if (!!user.org_access) {
      org = user.org_access.join(', ')
    }
    this.fullstoryService.setUser(user, org);
  }

  // Set new value for avatar_thumb_url in localStorage for currentUser
  updateAvatarInStorage(avatar_thumb_url: string) {
    localStorage.setItem('currentUser', JSON.stringify({
      ...JSON.parse(localStorage.getItem('currentUser') ?? '{}'),
      avatar_thumb_url,
    }));
  }

  mapUmsUserToInternalUserData(account: any, accessTokenRoles?: string[]): User | null {
    if(account == null || account == undefined) {
      return null;
    }
    const claims = account.idTokenClaims || {};
    const names = this.getNamesFromClaims(claims);
    // An empty access-token role array must still fall back to id-token claims;
    // `[]` is truthy, so a plain `||` would wrongly short-circuit to no roles.
    const rolesToCheck = accessTokenRoles?.length ? accessTokenRoles : (claims.roles || []);
    const roles = this.getRoleFromUmsRoles(rolesToCheck);
    if( roles == null) {
      console.warn('[UserService] No recognized roles for account:', account.username, 'roles checked:', rolesToCheck);
      return null;
    }
    const scope = roles === RoleType.OBSERVER ? 'channel' : 'airline';
    return {
      id: account.localAccountId,
      homeAccountId: account.homeAccountId,
      role: roles,
      org_access: claims.org_access?.map((org: string) => org.trim()),
      name: claims.name,
      first_name: names.first_name,
      last_name: names.last_name,
      email: claims.email,
      accepted_terms: true,
      scope,
    }
  }
  getRoleFromUmsRoles(roles: string[]): RoleType | null {
    if(!roles || roles.length === 0) {
      return null;
    }
    if(roles.includes('hub-delivery-spa.admin')) {
      return RoleType.ADMIN;
    } else if (roles.includes('hub-delivery-spa.superuser')) {
      return RoleType.SUPERUSER;
    } else if (roles.includes('hub-delivery-spa.collaborator')) {
      return RoleType.COLLABORATOR;
    } else if (roles.includes('hub-delivery-spa.observer')) {
      return RoleType.OBSERVER;
    } else if (roles.includes('hub-delivery-spa.community_participant')) {
      return RoleType.COMMUNITY_PARTICIPANT;
    } else {
      return null;
    }
  }
}
